'use client'

import { useEffect, useRef } from 'react'
import {
  ALARM_SOUND_LABELS,
  type AlarmSound,
  SYNTH_TONES,
  TimerAlarm,
  WAV_SOUND_MAP,
} from '@/components/editor/timer/timer-alarm'
import { NativeSelect } from '@/components/ui/native-select'
import { Toggle } from '@/components/ui/toggle'
import { useUserSettings } from '@/components/user-settings-context'
import type { UserSettings } from '@/lib/user-settings'
import { SettingRow } from './setting-row'

const THRESHOLD_OPTIONS = [
  { value: 60_000, label: '1 min' },
  { value: 180_000, label: '3 min' },
  { value: 300_000, label: '5 min' },
  { value: 600_000, label: '10 min' },
  { value: 900_000, label: '15 min' },
  { value: 1_200_000, label: '20 min' },
]

const ALARM_SOUND_GROUPS = [
  {
    label: 'Tones',
    options: SYNTH_TONES.map((t) => ({
      value: t,
      label: ALARM_SOUND_LABELS[t],
    })),
  },
  {
    label: 'Sound Effects',
    options: (Object.keys(WAV_SOUND_MAP) as AlarmSound[]).map((s) => ({
      value: s,
      label: ALARM_SOUND_LABELS[s],
    })),
  },
]

export function TimerSettings() {
  const { settings, updateSettings } = useUserSettings()
  const timer = settings.timer
  const alarmRef = useRef<TimerAlarm | null>(null)

  useEffect(() => {
    return () => alarmRef.current?.stopAlarm()
  }, [])

  const update = (
    partial: Partial<UserSettings['timer']>,
    options?: { immediate?: boolean },
  ) => updateSettings({ timer: partial }, options)

  const handleSoundChange = (value: string) => {
    const sound = value as AlarmSound
    update({ alarmSound: sound })

    // Play preview on selection — stop any in-progress playback first
    if (!alarmRef.current) {
      alarmRef.current = new TimerAlarm()
    }
    alarmRef.current.stopAlarm()
    alarmRef.current.initFromUserGesture()
    alarmRef.current.playPreview(sound)
  }

  const toggleThreshold = (thresholdMs: number) => {
    const current = timer.superAlarmThresholds
    if (current.includes(thresholdMs)) {
      update(
        { superAlarmThresholds: current.filter((t) => t !== thresholdMs) },
        { immediate: true },
      )
    } else if (current.length < 3) {
      update(
        { superAlarmThresholds: [...current, thresholdMs] },
        { immediate: true },
      )
    }
  }

  return (
    <div className="space-y-1">
      <SettingRow
        label="Record time overage"
        description="When using a timer after the clock hits 00:00:00 the time will become negative and begin to count up."
      >
        <Toggle
          checked={timer.recordOverage}
          onChange={(v) => update({ recordOverage: v }, { immediate: true })}
          aria-label="Toggle record time overage"
        />
      </SettingRow>

      <SettingRow
        label="Auto reset"
        description="Upon a successful Code Submit which passes all test cases, timer and/or stopwatch will reset to 00:00:00"
      >
        <Toggle
          checked={timer.autoReset}
          onChange={(v) =>
            v
              ? update({ autoReset: true }, { immediate: true })
              : update(
                  {
                    autoReset: false,
                    showSessionTransitionNotice: true,
                  },
                  { immediate: true },
                )
          }
          aria-label="Toggle auto reset on accepted submissions"
        />
      </SettingRow>

      {timer.autoReset && (
        <div className="flex items-center justify-between gap-4 py-2 pl-4">
          <span className="text-muted-foreground text-sm">Reset timer to</span>
          <div className="flex gap-1">
            {[
              { value: 'lastDuration', label: 'Last set duration' },
              { value: 'zero', label: 'Zero (no countdown)' },
            ].map((opt) => {
              const selected = timer.resetTimerTo === opt.value
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() =>
                    update(
                      {
                        resetTimerTo: opt.value as 'lastDuration' | 'zero',
                      },
                      { immediate: true },
                    )
                  }
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    selected
                      ? 'border-orange-500 bg-orange-500/10 text-orange-600'
                      : 'border-border text-muted-foreground hover:border-foreground/30 cursor-pointer'
                  }`}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>
        </div>
      )}

      <div className={timer.autoReset ? 'opacity-40' : ''}>
        <SettingRow
          label="Session transition notice"
          description="When auto reset is off, prompt before starting a new attempt so you can choose to reset or continue your timer."
        >
          <Toggle
            checked={timer.showSessionTransitionNotice}
            onChange={(v) =>
              update({ showSessionTransitionNotice: v }, { immediate: true })
            }
            disabled={timer.autoReset}
            aria-label="Toggle session transition notice"
          />
        </SettingRow>
      </div>

      <SettingRow
        label="Super alarm"
        description="Select up to 3 additional alarm checkpoints. When this is enabled an alarm sound of your choice will go off at the end of a Timer."
      >
        <Toggle
          checked={timer.superAlarmEnabled}
          onChange={(v) =>
            update({ superAlarmEnabled: v }, { immediate: true })
          }
          aria-label="Toggle super alarm"
        />
      </SettingRow>

      {timer.superAlarmEnabled && (
        <div className="flex flex-wrap gap-2 py-2 pl-1">
          {THRESHOLD_OPTIONS.map((opt) => {
            const selected = timer.superAlarmThresholds.includes(opt.value)
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggleThreshold(opt.value)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  selected
                    ? 'border-orange-500 bg-orange-500/10 text-orange-600'
                    : 'border-border text-muted-foreground hover:border-foreground/30'
                } ${
                  !selected && timer.superAlarmThresholds.length >= 3
                    ? 'cursor-not-allowed opacity-40'
                    : 'cursor-pointer'
                }`}
                disabled={!selected && timer.superAlarmThresholds.length >= 3}
              >
                {opt.label}
              </button>
            )
          })}
        </div>
      )}

      <SettingRow label="Alarm sound">
        <NativeSelect
          value={timer.alarmSound}
          onChange={handleSoundChange}
          groups={ALARM_SOUND_GROUPS}
        />
      </SettingRow>
    </div>
  )
}
